import React from 'react';
import { Award, Users, Heart, Sparkles, CheckCircle2, Star } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: <Award size={40} />, number: '12+', label: 'Years Experience' },
    { icon: <Users size={40} />, number: '500+', label: 'Events Delivered' },
    { icon: <Heart size={40} />, number: '1000+', label: 'Happy Clients' },
    { icon: <Star size={40} />, number: '100%', label: 'Dedication' },
  ];

  const whyChooseUs = [
    '12+ years of event planning and management experience',
    'Deep understanding of Gujarati celebrations and traditions',
    'Complete end-to-end planning and coordination',
    'Professional vendor and venue management',
    'Smooth, stress-free on-day execution',
    'Personalized service with attention to detail',
    'Practical pricing without compromising quality',
    'Hands-on care from planning to completion'
  ];

  return (
    <section id="about" className="py-20 md:py-32 bg-gradient-to-b from-white via-amber-50/30 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main About Section */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-base md:text-lg tracking-wide uppercase">
              About Made in Heaven
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6 leading-tight">
              Premium Event Planning.<br className="hidden md:block"/> Stress-Free Celebrations.
            </h2>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 space-y-6">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Made in Heaven</strong> is a premium event planning and management company dedicated to creating elegant, stress-free, and memorable celebrations. Founded by <strong>Anant Singh</strong>, who brings more than <strong>12 years of experience</strong> in event planning and management, the company is built on passion, trust, professionalism, and a deep understanding of what makes every event special.
            </p>
            
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              Being born and brought up in Gujarat, Anant understands the emotional, cultural, and family importance of celebrations for Gujarati families and businesses. From intimate gatherings to large weddings and corporate functions, every event deserves proper planning, seamless coordination, and careful execution.
            </p>
            
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              At Made in Heaven, we believe our clients should <strong>enjoy their special moments while we manage the pressure, planning, vendors, logistics, and execution</strong>. We combine experience, creativity, personal attention, and practical pricing to deliver events that are beautiful, smooth, and memorable.
            </p>
          </div>
        </div>

        {/* Founder Section */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl shadow-lg p-8 md:p-12 border border-amber-100">
            <div className="text-center mb-6">
              <div className="inline-block bg-amber-600 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide mb-4">
                Meet the Founder
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                Anant Singh
              </h3>
              <p className="text-amber-700 font-medium text-lg">
                Founder & Lead Event Planner
              </p>
            </div>
            
            <div className="space-y-4 text-center">
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                With over <strong className="text-gray-900">12 years of event planning and management experience</strong>, Anant Singh founded Made in Heaven to help clients celebrate without stress. Raised in Gujarat, he understands how meaningful events are for families and businesses, and brings passion, coordination, and hands-on care to every occasion.
              </p>
              
              <div className="flex flex-wrap justify-center gap-3 pt-4">
                <span className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  12+ Years Experience
                </span>
                <span className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  500+ Events
                </span>
                <span className="bg-white px-4 py-2 rounded-full text-sm font-medium text-gray-700 shadow-sm">
                  Gujarat Born & Raised
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-center border border-gray-100"
            >
              <div className="text-amber-600 flex justify-center mb-3">
                {stat.icon}
              </div>
              <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {stat.number}
              </div>
              <div className="text-sm md:text-base text-gray-600 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Why Choose Us */}
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why Choose Made in Heaven?
            </h3>
            <p className="text-lg text-gray-600">
              Experience, trust, and dedication to making your celebrations memorable
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {whyChooseUs.map((reason, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <CheckCircle2 className="text-amber-600 flex-shrink-0 mt-0.5" size={24} />
                <span className="text-gray-700 leading-relaxed">{reason}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
